import React, { useEffect, useState } from 'react';
import { IndicatorTypeSelector, ComparisonOperatorSelector } from '../Selectors';
import { ParameterEditor } from '../Form';
import { useIndicatorSchemas } from '../../../hooks/useTrendlineStrategy';
import { getDefaultIndicatorParameters } from '../../../utils/strategyUtils';

/**
 * IndicatorConditionBuilder - Form for building indicator conditions
 * 
 * @param {Object} props
 * @param {Object} props.condition - Current condition data
 * @param {Function} props.onChange - Change handler (updatedCondition) => void
 * @param {Object} props.errors - Validation errors
 * @param {boolean} props.disabled - Whether form is disabled
 * @param {Object} props.rulesSchema - Rules schema from API (optional)
 * @param {Object} props.indicatorSchemas - Indicator schemas from API (optional)
 */
const IndicatorConditionBuilder = ({
  condition,
  onChange,
  errors = {},
  disabled = false,
  rulesSchema = null,
  indicatorSchemas = null,
}) => {
  // Use passed schemas or fetch from hook as fallback
  const hookData = useIndicatorSchemas();
  const schemas = indicatorSchemas || hookData.schemas;
  const loading = indicatorSchemas ? false : hookData.loading;
  const schemaError = indicatorSchemas ? null : hookData.error;
  const [localCondition, setLocalCondition] = useState(condition || {
    conditionType: 'INDICATOR',
    indicatorType: '',
    operator: 'LESS_THAN',
    parameters: {},
  });

  // Sync with parent
  useEffect(() => {
    if (condition) {
      setLocalCondition(condition);
    }
  }, [condition]);

  // Handle indicator type change
  const handleIndicatorTypeChange = (indicatorType) => {
    const defaultParams = getDefaultIndicatorParameters(indicatorType);
    const updated = {
      ...localCondition,
      indicatorType,
      parameters: defaultParams,
    };
    setLocalCondition(updated);
    onChange(updated);
  };

  // Handle operator change
  const handleOperatorChange = (operator) => {
    const updated = { ...localCondition, operator };
    setLocalCondition(updated);
    onChange(updated);
  };

  // Handle parameter change
  const handleParameterChange = (paramName, value) => {
    const updated = {
      ...localCondition,
      parameters: {
        ...localCondition.parameters,
        [paramName]: value,
      },
    };
    setLocalCondition(updated);
    onChange(updated);
  };

  // Get parameter schema for current indicator type
  const getParameterSchema = () => {
    if (!schemas || !localCondition.indicatorType) return [];

    const indicatorSchema = schemas[localCondition.indicatorType];
    if (!indicatorSchema?.parameters) return [];

    return indicatorSchema.parameters.map(param => ({
      name: param.name,
      label: param.description || param.name,
      type: param.type,
      required: param.required,
      defaultValue: param.defaultValue,
      minValue: param.minValue,
      maxValue: param.maxValue,
      description: param.description,
      unit: param.unit,
      allowedValues: param.allowedValues,
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <span className="loading loading-spinner loading-md"></span>
      </div>
    );
  }

  if (schemaError) {
    return (
      <div className="alert alert-error mb-4">
        <span>Failed to load indicator schemas: {schemaError}</span>
      </div>
    );
  }

  const parameterSchema = getParameterSchema();

  return (
    <div>
      <h4 className="text-sm font-medium text-base-content/70 mb-2">
        Indicator Condition
      </h4>
      <div className="divider my-2"></div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Indicator Type */}
        <IndicatorTypeSelector
          value={localCondition.indicatorType}
          onChange={handleIndicatorTypeChange}
          error={errors.indicatorType}
          disabled={disabled}
          required
          schemas={schemas}
        />

        {/* Comparison Operator */}
        <ComparisonOperatorSelector
          value={localCondition.operator}
          onChange={handleOperatorChange}
          error={errors.operator}
          disabled={disabled || !localCondition.indicatorType}
          required
          operators={rulesSchema?.conditionTypes?.INDICATOR?.operators}
          rulesSchema={rulesSchema}
        />
      </div>

      {/* Parameters */}
      {localCondition.indicatorType && parameterSchema.length > 0 && (
        <div className="mt-4">
          <ParameterEditor
            title="Parameters"
            parameters={parameterSchema}
            values={localCondition.parameters}
            onChange={handleParameterChange}
            errors={errors.parameters || {}}
            disabled={disabled}
            columns={2}
          />
        </div>
      )}

      {/* Indicator Description */}
      {localCondition.indicatorType && schemas?.[localCondition.indicatorType] && (
        <div className="alert alert-info mt-4">
          <span className="text-sm">
            {schemas[localCondition.indicatorType].description}
          </span>
        </div>
      )}
    </div>
  );
};

export default IndicatorConditionBuilder;
